/* AZ Motorsport — header behaviors.
   Loaded with `defer` from layout/theme.liquid. Split out of inline <script>
   blocks in sections/header.liquid so the logic is cacheable and CSP-friendlier.
   The StickyHeader custom element intentionally stays inline in the section —
   it writes --header-height and must register as early as possible.

   Contents:
   1. Mobile search icon → opens burger drawer, focuses the in-drawer search
   2. <azm-countdown> — compact HH:MM:SS flash-sale timer (utility bar)
   3. Desktop mega menu — aria-expanded sync + Escape (ported from the old
      inline IIFE in the old azm-mega-menu snippet) + rail pane switching
   4. Mobile drawer push controller — lazy-fetches sections/az-menu-panels,
      pushes/pops level 2-3 panels, guards the drawer close button
*/

(function () {
  if (window.__azmHSearchWired) return;
  window.__azmHSearchWired = true;
  /* The mobile search icon opens the burger menu and focuses the search field pinned at its top. */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-azm-open-search]');
    if (!btn) return;
    e.preventDefault();
    var details = document.getElementById('Details-menu-drawer-container');
    if (!details) return;
    if (!details.open) {
      var summary = details.querySelector('summary');
      if (summary) { summary.click(); } else { details.open = true; }
    }
    setTimeout(function () {
      var input = document.querySelector('#menu-drawer .azms__input');
      if (input) { try { input.focus({ preventScroll: false }); } catch (err) { input.focus(); } }
    }, 400);
  });
})();

/* ── 2. <azm-countdown> ──────────────────────────────────────────────────
   Compact flash-sale timer for the utility bar. Same `data-timer` ISO
   contract as the theme's <count-down>, but renders one HH:MM:SS string
   (days folded into hours — the stock element can't do that: hours are %24
   and it never hides on expiry, see the plan's countdown ruling). On expiry
   (or an unparseable date) it hides the whole row: closest [data-azm-cd-row].
   Liquid already skips rendering when the end date is past; this covers
   mid-session expiry and cached pages. */
(function () {
  if (window.customElements.get('azm-countdown')) return;

  class AzmCountdown extends HTMLElement {
    connectedCallback() {
      this.endMs = Date.parse(this.getAttribute('data-timer') || '');
      this.digits = this.querySelector('[data-azm-cd-digits]') || this;
      if (isNaN(this.endMs)) { this.expire(); return; }
      this.tick = this.tick.bind(this);
      this.tick();
      this.interval = setInterval(this.tick, 1000);
    }

    disconnectedCallback() {
      if (this.interval) clearInterval(this.interval);
    }

    tick() {
      var left = Math.floor((this.endMs - Date.now()) / 1000);
      if (left <= 0) { this.expire(); return; }
      var h = Math.floor(left / 3600);
      var m = Math.floor((left % 3600) / 60);
      var s = left % 60;
      this.digits.textContent = this.pad(h) + ':' + this.pad(m) + ':' + this.pad(s);
    }

    pad(n) {
      return n < 10 ? '0' + n : String(n);
    }

    expire() {
      if (this.interval) clearInterval(this.interval);
      var row = this.closest('[data-azm-cd-row]') || this;
      row.hidden = true;
    }
  }

  window.customElements.define('azm-countdown', AzmCountdown);
})();

/* ── 3. Desktop mega menu ────────────────────────────────────────────────
   Panel OPEN/CLOSE stays pure CSS (:hover/:focus-within on
   .list-menu__has-mega — component-mega-menu.css). This script only:
   a) mirrors open state onto aria-expanded and closes on Escape via
      .azm-mm-force-closed (semantics identical to the old inline IIFE);
   b) drives 3-zone pane switching from the make rail: 80ms hover intent on
      mouseenter (kills diagonal-travel flicker), instant on focus,
      ArrowUp/Down + Home/End roving focus. Rail entries are real <a href>
      links — Enter navigates, JS-off users get the first pane server-rendered
      active and every link stays crawlable. */
(function () {
  function initMega() {
    var nav = document.querySelector('.header__inline-menu');
    if (!nav || nav.dataset.azmMegaWired) return;
    nav.dataset.azmMegaWired = '1';

    /* a) aria-expanded sync + Escape-to-close */
    nav.querySelectorAll('.list-menu__has-mega').forEach(function (li) {
      var trigger = li.querySelector(':scope > .header__menu-item');
      if (!trigger) return;
      var setExpanded = function (open) {
        trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
      };
      // Open signals (mouse re-enter also clears a prior Escape).
      li.addEventListener('mouseenter', function () {
        li.classList.remove('azm-mm-force-closed');
        setExpanded(true);
      });
      li.addEventListener('focusin', function () {
        if (!li.classList.contains('azm-mm-force-closed')) setExpanded(true);
      });
      // Close signals.
      li.addEventListener('mouseleave', function () {
        if (!li.contains(document.activeElement)) setExpanded(false);
      });
      li.addEventListener('focusout', function (e) {
        if (!li.contains(e.relatedTarget)) {
          setExpanded(false);
          li.classList.remove('azm-mm-force-closed');
        }
      });
      li.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' || e.key === 'Esc') {
          li.classList.add('azm-mm-force-closed');
          setExpanded(false);
          trigger.focus();
        }
      });
    });

    /* b) rail → pane switching */
    nav.querySelectorAll('[data-azm-mega]').forEach(function (mega) {
      var links = Array.prototype.slice.call(mega.querySelectorAll('.azm-mega__rail-link'));
      var panes = Array.prototype.slice.call(mega.querySelectorAll('.azm-mega__pane'));
      if (!links.length || !panes.length) return;
      var hoverTimer = null;

      function activate(idx) {
        links.forEach(function (l, i) {
          var on = i === idx;
          l.classList.toggle('is-active', on);
          if (on) { l.setAttribute('aria-current', 'true'); }
          else if (l.getAttribute('aria-current') === 'true') { l.removeAttribute('aria-current'); }
        });
        panes.forEach(function (p, i) {
          p.classList.toggle('is-active', i === idx);
        });
      }

      links.forEach(function (link, idx) {
        link.addEventListener('mouseenter', function () {
          window.clearTimeout(hoverTimer);
          hoverTimer = window.setTimeout(function () { activate(idx); }, 80);
        });
        link.addEventListener('mouseleave', function () {
          window.clearTimeout(hoverTimer);
        });
        link.addEventListener('focus', function () {
          window.clearTimeout(hoverTimer);
          activate(idx);
        });
        link.addEventListener('keydown', function (e) {
          var target = null;
          if (e.key === 'ArrowDown') target = idx + 1;
          else if (e.key === 'ArrowUp') target = idx - 1;
          else if (e.key === 'Home') target = 0;
          else if (e.key === 'End') target = links.length - 1;
          if (target === null) return;
          e.preventDefault();
          links[Math.max(0, Math.min(links.length - 1, target))].focus();
        });
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initMega);
  } else {
    initMega();
  }
  /* Theme Editor reloads the header section in place. */
  document.addEventListener('shopify:section:load', initMega);
})();

/* ── 4. Mobile drawer push controller ────────────────────────────────────
   The drawer's level-1 list ships inline (snippets/azm-mega-menu-drawer.liquid);
   levels 2-3 live in sections/az-menu-panels.liquid and are fetched ONCE per page
   via the section rendering API (the az-garage `?section_id=` precedent), then
   injected into [data-azm-panels-host]. Everything degrades: a push row is a real
   <a href> to its hub, so no-JS / slow-fetch / fetch-error users just navigate.

   Interop with MenuDrawer (assets/theme.js:352-636) — see the drawer snippet's
   header comment:
   - MenuDrawer binds a submenu-close handler to every <button> that exists when it
     is constructed. The level-1 close X is one of them, and the stock handler would
     run closeSubmenu() on the main <details> — which never clears the
     `overflow-hidden-*` body class (stranded scroll lock). A capture-phase listener
     on #menu-drawer therefore stops that click dead (the az-menu-search.liquid:63-66
     pattern) and routes it to summary.click(), the one true close path. Panel
     buttons are injected AFTER construction, so they are never bound — the same
     guard gives them identical behaviour anyway.
   - Escape is caught in the capture phase on the stage: with panels open it pops one
     level and stops there; at depth 0 it falls through to MenuDrawer's own close. */
(function () {
  var CACHE = { promise: null };

  function fetchPanels() {
    if (CACHE.promise) return CACHE.promise;
    var root = '/';
    try {
      if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) root = window.Shopify.routes.root;
    } catch (e) { /* no Shopify global (rare) — fall back to the domain root */ }
    root = String(root).replace(/\/+$/, '');
    CACHE.promise = fetch(root + '/?section_id=az-menu-panels', { headers: { Accept: 'text/html' } })
      .then(function (r) {
        if (!r.ok) throw new Error('az-menu-panels: HTTP ' + r.status);
        return r.text();
      })
      .then(function (html) {
        var payload = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-azm-panels]');
        if (!payload) throw new Error('az-menu-panels: payload missing');
        return payload.innerHTML;
      });
    /* let a failed fetch be retried on the next open (rows navigate meanwhile) */
    CACHE.promise.catch(function () { CACHE.promise = null; });
    return CACHE.promise;
  }

  function initDrawerPush() {
    var details = document.getElementById('Details-menu-drawer-container');
    if (!details) return;
    var drawer = details.querySelector('#menu-drawer');
    var stage = details.querySelector('[data-azm-panels-host]');
    if (!drawer || !stage || stage.dataset.azmWired) return;
    stage.dataset.azmWired = '1';

    var summary = details.querySelector('summary.header__icon--menu') || details.querySelector('summary');
    var underlay = details.querySelector('.menu-drawer__navigation-container');
    var loadingText = stage.getAttribute('data-azm-loading-text') || '';
    var backText = stage.getAttribute('data-azm-back-text') || '';
    var stack = [];
    var injected = false;
    var injectPromise = null;
    var trapTimer = null;

    /* The stage clips panels with overflow:hidden, but overflow:hidden boxes are
       still PROGRAMMATICALLY scrollable: focusing the back button while its panel
       is mid-slide (still at translateX(100%)) makes the browser scroll-chase it,
       leaving stage.scrollLeft at one full width — the panel then lives one width
       to the LEFT forever and the drawer looks like it "slid off screen". The
       stage must never scroll (panels scroll their own overflow-y), so pin it. */
    stage.addEventListener('scroll', function () {
      if (stage.scrollLeft) stage.scrollLeft = 0;
      if (stage.scrollTop) stage.scrollTop = 0;
    });

    function panelFor(handle) {
      if (!handle || !/^[a-z0-9][a-z0-9_-]*$/i.test(handle)) return null;
      return stage.querySelector('[data-azm-panel="' + handle + '"]');
    }

    function setUnderlayInert(on) {
      if (!underlay) return;
      if (on) {
        underlay.setAttribute('aria-hidden', 'true');
        underlay.inert = true;
      } else {
        underlay.removeAttribute('aria-hidden');
        underlay.inert = false;
      }
    }

    function focusPanel(panel) {
      var target = panel.querySelector('[data-azm-back]') || panel;
      /* preventScroll: the panel is mid-slide when this runs — a plain focus()
         scroll-chases the target inside the overflow:hidden stage (see the
         scroll pin above for the failure mode this caused). */
      try { target.focus({ preventScroll: true }); } catch (e) {
        try { target.focus(); } catch (e2) { /* ignore */ }
      }
      stage.scrollLeft = 0;
      /* Stock trapFocus() re-focuses WITHOUT preventScroll, so it only runs once
         the slide is over (transition is .32s) and the chase would compute to 0. */
      if (typeof window.trapFocus === 'function') {
        window.clearTimeout(trapTimer);
        trapTimer = window.setTimeout(function () {
          if (!panel.classList.contains('is-active')) return;
          try { window.trapFocus(panel, target); } catch (e) { /* ignore */ }
          stage.scrollLeft = 0;
        }, 360);
      }
    }

    function skeleton() {
      return stage.querySelector('[data-azm-skeleton]');
    }

    function showSkeleton(label) {
      var existing = skeleton();
      if (existing) existing.remove();
      var el = document.createElement('div');
      el.className = 'azm-mdr__panel azm-mdr__panel--skeleton is-active';
      el.setAttribute('data-azm-skeleton', '');
      el.setAttribute('tabindex', '-1');
      el.innerHTML =
        '<div class="azm-mdr__panel-head">' +
        '<button type="button" class="azm-mdr__back" data-azm-back></button>' +
        '<span class="azm-mdr__panel-title"></span>' +
        '</div>' +
        '<div class="azm-mdr__panel-body"><p class="azm-mdr__loading"></p></div>';
      el.querySelector('.azm-mdr__back').textContent = backText;
      el.querySelector('.azm-mdr__panel-title').textContent = label || '';
      el.querySelector('.azm-mdr__loading').textContent = loadingText;
      stage.appendChild(el);
      stage.classList.add('is-open');
      focusPanel(el);
      setUnderlayInert(true);
    }

    function push(panel) {
      var current = stack.length ? panelFor(stack[stack.length - 1]) : null;
      if (current) {
        current.classList.remove('is-active');
        current.classList.add('is-under');
      }
      panel.classList.remove('is-under');
      panel.classList.add('is-active');
      stack.push(panel.getAttribute('data-azm-panel'));
      stage.classList.add('is-open');
      focusPanel(panel);
      setUnderlayInert(true);
    }

    function pop() {
      var sk = skeleton();
      if (sk) sk.remove();

      var handle = stack.pop();
      if (handle) {
        var closing = panelFor(handle);
        if (closing) {
          closing.classList.remove('is-active');
          closing.classList.remove('is-under');
        }
      }

      var prev = stack.length ? panelFor(stack[stack.length - 1]) : null;
      if (prev) {
        prev.classList.remove('is-under');
        prev.classList.add('is-active');
        focusPanel(prev);
        return;
      }

      window.clearTimeout(trapTimer);
      stage.classList.remove('is-open');
      stage.scrollLeft = 0;
      setUnderlayInert(false);
      var opener = handle && underlay ? underlay.querySelector('a[data-azm-push="' + handle + '"]') : null;
      /* trapFocus dereferences its 2nd argument, so never hand it null. */
      if (typeof window.trapFocus === 'function') {
        try { window.trapFocus(details, opener || summary || details); return; } catch (e) { /* fall through */ }
      }
      if (opener) { try { opener.focus(); } catch (e) { /* ignore */ } }
    }

    function reset() {
      window.clearTimeout(trapTimer);
      var sk = skeleton();
      if (sk) sk.remove();
      stage.scrollLeft = 0;
      if (!stack.length && !stage.classList.contains('is-open')) return;
      stack.length = 0;
      Array.prototype.forEach.call(stage.querySelectorAll('.azm-mdr__panel'), function (p) {
        p.classList.remove('is-active');
        p.classList.remove('is-under');
      });
      stage.classList.remove('is-open');
      setUnderlayInert(false);
    }

    function ensurePanels() {
      if (injected) return Promise.resolve();
      if (injectPromise) return injectPromise;
      injectPromise = fetchPanels().then(function (html) {
        if (!injected) {
          stage.insertAdjacentHTML('afterbegin', html);
          injected = true;
        }
      });
      injectPromise.catch(function () { injectPromise = null; });
      return injectPromise;
    }

    /* Close X (level 1 and every panel head) — stop the stock MenuDrawer button
       handler and use the summary instead. Capture phase = fires before the
       button's own listeners. */
    drawer.addEventListener(
      'click',
      function (e) {
        var closeBtn = e.target && e.target.closest ? e.target.closest('[data-azm-drawer-close]') : null;
        if (!closeBtn) return;
        e.preventDefault();
        e.stopImmediatePropagation();
        reset();
        if (summary) summary.click();
      },
      true
    );

    drawer.addEventListener('click', function (e) {
      if (!e.target || !e.target.closest) return;

      var back = e.target.closest('[data-azm-back]');
      if (back && stage.contains(back)) {
        e.preventDefault();
        pop();
        return;
      }

      var row = e.target.closest('a[data-azm-push]');
      if (!row) return;
      var handle = row.getAttribute('data-azm-push');

      if (injected) {
        var panel = panelFor(handle);
        if (panel) {
          e.preventDefault();
          push(panel);
        }
        return; /* no panel for this handle -> let the link navigate */
      }

      /* Panels still in flight: hold the user on a loading panel rather than
         throwing away the tap, and navigate if the fetch never lands. */
      e.preventDefault();
      var labelEl = row.querySelector('.azm-mdr__row-label, .azm-mdr__make-label');
      showSkeleton(((labelEl || row).textContent || '').trim());
      var href = row.href;
      ensurePanels()
        .then(function () {
          var sk = skeleton();
          if (!sk) return; /* user backed out while loading */
          var panel = panelFor(handle);
          sk.remove();
          if (panel) { push(panel); } else { window.location.href = href; }
        })
        .catch(function () {
          var sk = skeleton();
          if (sk) sk.remove();
          window.location.href = href;
        });
    });

    /* Escape: pop one level while panels are open, otherwise let MenuDrawer close
       the whole drawer (its handler listens on keyup, bubbling, at <header-drawer>). */
    stage.addEventListener(
      'keyup',
      function (e) {
        if (e.key !== 'Escape' && e.key !== 'Esc') return;
        if (!stack.length && !skeleton()) return;
        e.stopPropagation();
        pop();
      },
      true
    );

    /* Prefetch: the hamburger is the only way in on mobile. */
    if (summary) {
      summary.addEventListener('pointerenter', ensurePanels);
      summary.addEventListener('touchstart', ensurePanels, { passive: true });
    }

    details.addEventListener('toggle', function () {
      if (details.hasAttribute('open')) { ensurePanels(); } else { reset(); }
    });

    /* Close starts when `menu-opening` is dropped — 400ms before [open] goes away,
       so reset there too and the drawer never re-opens on a stale panel. */
    if (window.MutationObserver) {
      new MutationObserver(function () {
        if (!details.classList.contains('menu-opening')) reset();
      }).observe(details, { attributes: true, attributeFilter: ['class'] });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initDrawerPush);
  } else {
    initDrawerPush();
  }
  document.addEventListener('shopify:section:load', initDrawerPush);
})();
